import { action, makeObservable, observable, runInAction } from 'mobx';
import { Minuta, Processo, TipoMinuta } from '../modelo';
import { SituacaoMinuta } from '../modelo/SituacaoMinuta';
import StorageUtil from '../utils/StorageUtils';
import UsuarioStore from './UsuarioStore';

/**
 * Store do Escaninho de Pauta.
 *
 * Mantém em memória os campos de revisão recebidos da API de minutas e persiste
 * localmente apenas os controles internos que ainda não possuem persistência no GE:
 *  - revisor responsável por cada processo;
 *  - status da revisão de cada minuta;
 *  - observações de revisão (OBS_REV) e de estrutura/secretaria (OBS_EST);
 *  - movimentação (campo de controle interno, NÃO mexe no fluxo real do GE);
 *  - se o processo está autorizado para inclusão em pauta pela secretaria;
 *  - se o processo foi arquivado do controle de pauta.
 */

interface IPersistencia {
    obsEstPorProcesso: Record<string, string>;
    movimentacaoPorProcesso: Record<string, string>;
    arquivadoPorProcesso: Record<string, boolean>;
}

const PREFIXO_STORAGE = 'pauta-revisao';

class PautaRevisaoStore {
    revisorPorProcesso = observable.map<string, string>();
    statusRevisaoPorMinuta = observable.map<string, number>();
    autorizadoPautaPorProcesso = observable.map<string, boolean>();
    obsRevPorProcesso = observable.map<string, string>();
    obsEstPorProcesso = observable.map<string, string>();
    movimentacaoPorProcesso = observable.map<string, string>();
    arquivadoPorProcesso = observable.map<string, boolean>();

    /** Estado de UI (não persistido): exibir também os processos arquivados na lista. */
    mostrarArquivados = false;

    /** Estado de UI (não persistido): filtro por situação de autorização para pauta. */
    filtroAutorizacao: 'todos' | 'autorizados' | 'naoautorizados' = 'naoautorizados';

    private gabineteHidratado: string | undefined;

    constructor() {
        makeObservable(this, {
            revisorPorProcesso: observable,
            statusRevisaoPorMinuta: observable,
            autorizadoPautaPorProcesso: observable,
            obsRevPorProcesso: observable,
            obsEstPorProcesso: observable,
            movimentacaoPorProcesso: observable,
            arquivadoPorProcesso: observable,
            mostrarArquivados: observable,
            filtroAutorizacao: observable,
            setRevisor: action,
            setStatusRevisao: action,
            setAutorizado: action,
            setObsRev: action,
            setObsEst: action,
            setMovimentacao: action,
            setArquivado: action,
            setMostrarArquivados: action,
            setFiltroAutorizacao: action,
            hidratar: action,
        });
    }

    private get nomeStorage() {
        const gabinete = UsuarioStore.gabinete || 'sem-gabinete';
        return `${PREFIXO_STORAGE}-${gabinete}`;
    }

    private chaveMinuta(idProcesso: number | string, numeroMinuta: number | string) {
        return `${idProcesso}::${numeroMinuta}`;
    }

    private minutaValidaPauta(minuta: Minuta): boolean {
        if (minuta.situacaoMinuta?.codigo === SituacaoMinuta.EM_ELABORACAO) {
            return false;
        }

        const descricaoTipo = (minuta.tipo?.descricao || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .toLowerCase();
        const ehVoto = minuta.tipo?.codigo === TipoMinuta.VOTO ||
            minuta.tipo?.codigo === TipoMinuta.VOTO_AGUIA ||
            descricaoTipo.startsWith('voto');

        return !(ehVoto &&
            minuta.situacaoMinuta?.codigo === SituacaoMinuta.LIBERADA_PARA_ASSINATURA_OU_INCLUSAO_EM_PAUTA &&
            !minuta.vistoLiberado);
    }

    /**
     * Carrega no estado local os campos de revisão vindos da API de processo/minuta.
     * Não persiste no localStorage para evitar sobrescrever dados do backend.
     */
    sincronizarComProcesso(processo?: Processo) {
        if (!processo) {
            return;
        }

        const id = String(processo.identificadorProcesso);
        const minutasComNumero = (processo.minutas || []).filter(minuta =>
            minuta &&
            minuta.numero &&
            this.minutaValidaPauta(minuta)
        );

        const revisor = minutasComNumero.find(minuta => minuta.cpfRevisorPauta)?.cpfRevisorPauta;
        if (revisor && !this.revisorPorProcesso.get(id)) {
            this.revisorPorProcesso.set(id, revisor);
        }

        const textoRevisao = minutasComNumero.find(minuta => minuta.textoRevisaoPauta)?.textoRevisaoPauta;
        if (textoRevisao && !this.obsRevPorProcesso.get(id)) {
            this.obsRevPorProcesso.set(id, textoRevisao);
        }

        // Usa filtro mais amplo (todas as minutas, exceto EM_ELABORACAO) para ser consistente
        // com o salvarAutorizacao, que também persiste em todas as minutas do processo.
        const minutasParaLerAutorizacao = (processo.minutas || []).filter(minuta =>
            minuta &&
            minuta.numero &&
            minuta.situacaoMinuta?.codigo !== SituacaoMinuta.EM_ELABORACAO
        );
        const autorizado = minutasParaLerAutorizacao.some(minuta => minuta.autorizacaoPauta === true);
        if (!this.autorizadoPautaPorProcesso.has(id)) {
            this.autorizadoPautaPorProcesso.set(id, autorizado);
        }

        minutasComNumero.forEach(minuta => {
            const numeroStatus = minuta.statusRevisao?.numero;
            const chave = this.chaveMinuta(id, minuta.numero);
            if (numeroStatus !== undefined && numeroStatus !== null && !this.statusRevisaoPorMinuta.has(chave)) {
                this.statusRevisaoPorMinuta.set(chave, numeroStatus);
            }
        });
    }

    /** Carrega do localStorage apenas os controles internos sem persistência no GE. */
    hidratar() {
        const gabinete = UsuarioStore.gabinete || 'sem-gabinete';
        if (this.gabineteHidratado === gabinete) {
            return;
        }
        this.gabineteHidratado = gabinete;

        const dados: IPersistencia | null = StorageUtil.getObject(this.nomeStorage);
        runInAction(() => {
            // Dados de revisão são fonte do GE; não reutilizar valores antigos do navegador.
            this.revisorPorProcesso.clear();
            this.statusRevisaoPorMinuta.clear();
            this.autorizadoPautaPorProcesso.clear();
            this.obsRevPorProcesso.clear();
            this.obsEstPorProcesso.replace(dados?.obsEstPorProcesso ?? {});
            this.movimentacaoPorProcesso.replace(dados?.movimentacaoPorProcesso ?? {});
            this.arquivadoPorProcesso.replace(dados?.arquivadoPorProcesso ?? {});
        });
        // Sobrescreve o formato anterior e remove os campos de revisão já armazenados.
        this.persistir();
    }

    private persistir() {
        const dados: IPersistencia = {
            obsEstPorProcesso: Object.fromEntries(this.obsEstPorProcesso),
            movimentacaoPorProcesso: Object.fromEntries(this.movimentacaoPorProcesso),
            arquivadoPorProcesso: Object.fromEntries(this.arquivadoPorProcesso),
        };
        StorageUtil.setItem(this.nomeStorage, JSON.stringify(dados));
    }

    private setTextoMapa(
        mapa: ReturnType<typeof observable.map<string, string>>,
        chave: string,
        valor: string | undefined,
        persistirLocalmente = true,
    ) {
        if (valor && valor.trim().length > 0) {
            mapa.set(chave, valor);
        } else {
            mapa.delete(chave);
        }
        if (persistirLocalmente) {
            this.persistir();
        }
    }

    private setBoolMapa(
        mapa: ReturnType<typeof observable.map<string, boolean>>,
        chave: string,
        valor: boolean,
        persistirLocalmente = true,
    ) {
        if (valor) {
            mapa.set(chave, true);
        } else {
            mapa.delete(chave);
        }
        if (persistirLocalmente) {
            this.persistir();
        }
    }

    // ----- Revisor -----
    getRevisor(idProcesso: number | string): string | undefined {
        return this.revisorPorProcesso.get(String(idProcesso));
    }

    setRevisor(idProcesso: number | string, revisor: string | undefined) {
        this.setTextoMapa(this.revisorPorProcesso, String(idProcesso), revisor, false);
    }

    /**
     * Revisor do processo, usando o valor já carregado em memória e, na ausência dele, o
     * gravado nas minutas. Permite exibir o revisor em telas que não sincronizam a store,
     * como o Escaninho de Julgamento.
     */
    revisorDoProcesso(processo?: Processo): string | undefined {
        if (!processo) {
            return undefined;
        }

        const emMemoria = this.getRevisor(processo.identificadorProcesso);
        if (emMemoria) {
            return emMemoria;
        }

        return (processo.minutas || [])
            .filter(minuta => minuta?.numero && this.minutaValidaPauta(minuta))
            .find(minuta => minuta.cpfRevisorPauta)?.cpfRevisorPauta;
    }

    // ----- Status da revisão (por minuta) -----
    getStatusRevisao(idProcesso: number | string, numeroMinuta: number | string): number | undefined {
        return this.statusRevisaoPorMinuta.get(this.chaveMinuta(idProcesso, numeroMinuta));
    }

    setStatusRevisao(idProcesso: number | string, numeroMinuta: number | string, codigoStatus: number | undefined) {
        const chave = this.chaveMinuta(idProcesso, numeroMinuta);
        if (codigoStatus === undefined || codigoStatus === null) {
            this.statusRevisaoPorMinuta.delete(chave);
        } else {
            this.statusRevisaoPorMinuta.set(chave, codigoStatus);
        }
    }

    /** Códigos de status de revisão presentes no processo (todas as minutas). */
    statusRevisaoDoProcesso(idProcesso: number | string, numerosMinutas: Array<number | string>): number[] {
        return numerosMinutas
            .map(numero => this.getStatusRevisao(idProcesso, numero))
            .filter((codigo): codigo is number => codigo !== undefined);
    }

    // ----- Autorizado para pauta -----
    isAutorizado(idProcesso: number | string): boolean {
        return this.autorizadoPautaPorProcesso.get(String(idProcesso)) ?? false;
    }

    setAutorizado(idProcesso: number | string, autorizado: boolean) {
        // Mantém inclusive o valor false em memória para que uma resposta antiga da
        // minuta não restaure a autorização antes da próxima recarga do backend.
        this.autorizadoPautaPorProcesso.set(String(idProcesso), autorizado);
    }

    // ----- Observações -----
    getObsRev(idProcesso: number | string): string {
        return this.obsRevPorProcesso.get(String(idProcesso)) ?? '';
    }

    setObsRev(idProcesso: number | string, texto: string | undefined) {
        this.setTextoMapa(this.obsRevPorProcesso, String(idProcesso), texto, false);
    }

    getObsEst(idProcesso: number | string): string {
        return this.obsEstPorProcesso.get(String(idProcesso)) ?? '';
    }

    setObsEst(idProcesso: number | string, texto: string | undefined) {
        this.setTextoMapa(this.obsEstPorProcesso, String(idProcesso), texto);
    }

    // ----- Movimentação (controle interno) -----
    getMovimentacao(idProcesso: number | string): string {
        return this.movimentacaoPorProcesso.get(String(idProcesso)) ?? '';
    }

    setMovimentacao(idProcesso: number | string, texto: string | undefined) {
        this.setTextoMapa(this.movimentacaoPorProcesso, String(idProcesso), texto);
    }

    // ----- Arquivamento -----
    isArquivado(idProcesso: number | string): boolean {
        return this.arquivadoPorProcesso.get(String(idProcesso)) ?? false;
    }

    setArquivado(idProcesso: number | string, arquivado: boolean) {
        this.setBoolMapa(this.arquivadoPorProcesso, String(idProcesso), arquivado);
    }

    setMostrarArquivados(valor: boolean) {
        this.mostrarArquivados = valor;
    }

    setFiltroAutorizacao(valor: 'todos' | 'autorizados' | 'naoautorizados') {
        this.filtroAutorizacao = valor;
    }
}

export default new PautaRevisaoStore();
